import React, { useState } from 'react';
import ProjectSlideCard from '../../components/ProjectSlideCard';
import PropTypes from 'prop-types';
import bem from '../../helpers/bem';
import useLocalization from '../../hooks/useLocalization';
import { useSwipeable } from 'react-swipeable';
import useTranslation from '../../hooks/useTranslation';

const ProjectSlider = ({
  title,
  projects
}) => {
  const [entries, setProjectEntries] = useState(projects);
  const [activeEntry, setActiveEntry] = useState(0);
  const __ = useTranslation();

  // Icons
  const local = useLocalization();
  const iconArrowLeft = `${local('svgSprite')}#arrow-left`;
  const iconArrowRight = `${local('svgSprite')}#arrow-right`;

  // If there are fewer than 6 entries, the array is padded with existing ones.
  if (entries.length < 6) {
    const clonedEntries = [...entries];
    while (clonedEntries.length < 6) clonedEntries.push(...entries);
    setProjectEntries(clonedEntries);
  }

  // Get the slide offset value.
  const getSlideOffset = index => {
    const delta = index - activeEntry;

    if (activeEntry === 0 && index === entries.length - 1) return -1;
    if (activeEntry === 0 && index === entries.length - 2) return -2;
    if (activeEntry === 1 && index === entries.length - 1) return -2;

    if (activeEntry === entries.length - 1 && index === 0) return 1;
    if (activeEntry === entries.length - 1 && index === 1) return 2;
    if (activeEntry === entries.length - 1 && index === 2) return 3;

    if (activeEntry === entries.length - 2 && index === 0) return 2;
    if (activeEntry === entries.length - 2 && index === 1) return 3;

    if (activeEntry === entries.length - 3 && index === 0) return 3;

    if (delta < -2) return -2;
    if (delta > 3) return 3;
    return delta;
  };

  // Navigate Left
  const navigateLeft = () => {
    if (activeEntry > 0) setActiveEntry(activeEntry - 1);
    else setActiveEntry(entries.length - 1);
  };

  // Navigate Right
  const navigateRight = () => {
    if (activeEntry < entries.length - 1) setActiveEntry(activeEntry + 1);
    else setActiveEntry(0);
  };

  // Add swipe handlers.
  const swipeHandlers = useSwipeable({
    onSwipedLeft: navigateRight,
    onSwipedRight: navigateLeft
  });

  return (
    <div className='container'>
      <div className='tw-flex tw-justify-between tw-items-center'>
        <h2>{title}</h2>
        <div className={bem('s-project-slider', 'header-controls')}>
          <button
            className={bem('c-button', '', { 'outline-black': true, icon: true })}
            onClick={navigateLeft}
            title={__('section-project-slider-navigate-left')}
            aria-label={__('section-project-slider-navigate-left')}
          >
            <svg viewBox='0 0 200 200'>
              <use href={iconArrowLeft}></use>
            </svg>
          </button>
          <button
            className={bem('c-button', '', { 'outline-black': true, icon: true })}
            onClick={navigateRight}
            title={__('section-project-slider-navigate-right')}
            aria-label={__('section-project-slider-navigate-right')}
          >
            <svg viewBox='0 0 200 200'>
              <use href={iconArrowRight}></use>
            </svg>
          </button>
        </div>
      </div>
      <div className='s-project-slider__wrapper' {...swipeHandlers}>
        {entries.map((entry, index) => (
          <ProjectSlideCard
            customer={entry.customer}
            projectName={entry.projectName}
            excerpt={entry.excerpt}
            image={entry.image}
            permalink={entry.permalink}
            dataOffset={getSlideOffset(index)}
            className={bem('s-project-slider', 'entry')}
            key={index}
          ></ProjectSlideCard>
        ))}
      </div>
    </div>
  );
};

ProjectSlider.propTypes = {
  title: PropTypes.string,
  projects: PropTypes.array
};

export default ProjectSlider;
